import { useEffect, useState } from "react";
import { loadRuntimeConfig } from "../../lib/config";
import { getLoginDefaults, useAppStore } from "../../store/app";
import styles from "./LoginScreen.module.css";

export function LoginScreen() {
  const defaults = getLoginDefaults();
  const connectMqtt = useAppStore((s) => s.connectMqtt);
  const seedDemoData = useAppStore((s) => s.seedDemoData);
  const error = useAppStore((s) => s.error);
  const connectionPhase = useAppStore((s) => s.connectionPhase);

  const [wsUrl, setWsUrl] = useState(defaults.wsUrl || "");
  const [username, setUsername] = useState(defaults.mqttUser || "web-console");
  const [password, setPassword] = useState("");
  const [operatorName, setOperatorName] = useState(defaults.operatorName);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    void loadRuntimeConfig().then((cfg) => {
      if (cfg.mqttWsUrl && !defaults.wsUrl) {
        setWsUrl(cfg.mqttWsUrl);
      }
    });
  }, [defaults.wsUrl]);

  const busy = connectionPhase === "connecting";

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!wsUrl.trim() || !username.trim() || !password) {
      setLocalError("MQTT URL, username, and password are required.");
      return;
    }
    try {
      await connectMqtt(
        { wsUrl: wsUrl.trim(), username: username.trim(), password },
        operatorName.trim(),
      );
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : "Connection failed");
    }
  };

  return (
    <div className={styles.screen}>
      <form className={styles.card} onSubmit={(e) => void onSubmit(e)}>
        <div className={styles.brand}>
          <div className={styles.orb} aria-hidden />
          <h1>A.A.R.I.A.</h1>
          <p>Command Console</p>
        </div>

        <div className={styles.field}>
          <label htmlFor="wsUrl">WebSocket URL</label>
          <input
            id="wsUrl"
            type="url"
            value={wsUrl}
            onChange={(e) => setWsUrl(e.target.value)}
            autoComplete="off"
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="username">MQTT username</label>
          <input
            id="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="password">MQTT password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="operator">Your name (optional)</label>
          <input
            id="operator"
            value={operatorName}
            onChange={(e) => setOperatorName(e.target.value)}
            placeholder="Alice"
          />
        </div>

        <button type="submit" className={styles.submit} disabled={busy}>
          {busy ? "Connecting…" : "Connect"}
        </button>

        {(localError || error) && (
          <p className={styles.error} role="alert">
            {localError || error}
          </p>
        )}

        <p className={styles.hint}>
          Credentials stay in this browser tab. Pair once per device on the AARIA desk.
        </p>

        {import.meta.env.DEV ? (
          <button
            type="button"
            className={styles.demo}
            onClick={() => void seedDemoData()}
          >
            Skip login — demo mode (dev)
          </button>
        ) : null}
      </form>
    </div>
  );
}
